import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Default (English) messages for the Odoo environments card and for the
 * per-branch page it links to.
 *
 * One register for both on purpose: the page is the card's row opened up, so
 * the two say "Health", "Sync" or "Address" with the very same words. Splitting
 * them into two refs would let those words drift.
 *
 * These live here rather than in @waadoo/backstage-i18n because that package
 * imports translation refs from published plugin packages, and this card is
 * part of the app itself — the dependency would be circular. If the card ever
 * becomes a standalone plugin, both halves can move there.
 */
export const odooEnvironmentsTranslationRef = createTranslationRef({
  id: 'odoo-environments',
  messages: {
    'card.title': 'Environments',
    'card.subtitle': 'One Odoo environment per development branch',
    'card.refresh': 'Refresh',
    'card.empty':
      'No environment for this project. Push a `dev-…` branch to create one.',
    'column.branch': 'Branch',
    'column.ci': 'CI',
    'column.health': 'Health',
    'column.sync': 'Sync',
    'column.url': 'Address',
    'column.links': 'Links',
    'ci.success': 'Success',
    'ci.failed': 'Failed',
    'ci.running': 'Running',
    'ci.pending': 'Pending',
    'ci.canceled': 'Canceled',
    'ci.skipped': 'Skipped',
    'ci.unavailable': 'Unavailable',
    // ArgoCD's own health and sync vocabulary. The English side deliberately
    // repeats the API's wording: it is what the ArgoCD UI shows one click away,
    // so anything else would make the two disagree.
    'health.healthy': 'Healthy',
    'health.progressing': 'Progressing',
    'health.degraded': 'Degraded',
    'health.suspended': 'Suspended',
    'health.missing': 'Missing',
    'health.unknown': 'Unknown',
    'sync.synced': 'Synced',
    'sync.outOfSync': 'Out of sync',
    'sync.unknown': 'Unknown',
    'row.openInArgocd': 'Open in ArgoCD',
    'row.openTerminals': 'Open a terminal',
    'row.openInGrafana': 'Monitoring (Grafana)',
    'row.openPipelines': 'CI/CD pipelines for this branch',
    'row.openPipelineRun': 'Open this pipeline run',
    'terminal.sectionApp': 'Application',
    'terminal.sectionDb': 'Database',
    'terminal.loading': 'Loading pods…',
    'terminal.empty': 'No running pod',
    'terminal.error': 'Pods unavailable',
    // The per-branch page.
    'page.environment': 'Environment',
    'page.project': 'Project',
    'page.revision': 'Revision',
    'page.notFound':
      'No environment for branch {{branch}} in project {{project}}. It may have just been deleted.',
    // The access card, directly under the identity card: the administrator
    // credentials of the environment, read from the Kubernetes Secret the chart
    // generates with it. "Show" and not "Reveal": the same word the browser's
    // own password fields use, so the button needs no learning.
    'credentials.title': 'Odoo access',
    'credentials.subtitle': 'Administrator sign-in for this environment',
    'credentials.login': 'Login',
    'credentials.password': 'Password',
    'credentials.reveal': 'Show',
    'credentials.hide': 'Hide',
    'credentials.copy': 'Copy',
    'credentials.copied': 'Copied',
    // Names the Secret rather than saying "a Kubernetes Secret": where the
    // password lives is the point of the card, and the name is what makes it
    // checkable with kubectl by whoever wants to.
    'credentials.source':
      'Generated with the environment and kept in the Kubernetes Secret {{secret}}.',
    // A fact, not a failure — the register of `backups.notConfigured`: nothing
    // is broken, the environment simply has not initialised yet.
    'credentials.notConfigured':
      'No administrator credentials for this environment. They are generated on its first initialisation.',
    'credentials.unavailable': 'The credentials cannot be read right now.',
    'credentials.retry': 'Try again',
    // The clipboard is unavailable in a non-secure context and can be refused
    // outright; the sentence has to say what to do instead, or the button reads
    // as broken.
    'credentials.copyFailed':
      'The browser refused the copy: show the password and select it.',
    // The header's branch switcher.
    'page.switchBranch': 'Switch branch',
    'page.branches': 'Branches',
    'history.title': 'Deployment history',
    'history.subtitle':
      'Every revision ArgoCD has deployed on this branch, newest first',
    'history.columnRevision': 'Revision',
    'history.columnDeployedAt': 'Deployed',
    'history.empty': 'No deployment recorded for this branch yet.',
    'history.openRevision': 'Open this revision on the forge',
    'logs.title': 'Logs',
    'logs.subtitle': 'Last {{lines}} lines of {{pod}}, read once',
    // No "loading" message: the panel shows a skeleton while it reads, exactly
    // as the card does. Declaring one here would be a key nothing asks for.
    'logs.noPods': 'No running pod to read logs from.',
    'logs.unavailable': 'The logs cannot be shown here. Open them in ArgoCD.',
    'logs.openInArgocd': 'Open the logs in ArgoCD',
    // The backups panel. "Listing" throughout, never "backup", for the run
    // itself: what the button starts is an inventory of the bucket, and calling
    // it "a backup" would suggest the click creates one.
    'backups.title': 'Backups',
    'backups.subtitle': 'Backup sets available for this environment',
    'backups.notConfigured': 'No backup is configured for this environment.',
    'backups.noRun':
      'No listing has been made yet. Start one to see the available backups.',
    'backups.running': 'Listing in progress…',
    'backups.lastRun': 'Last listing: {{date}}',
    'backups.runFailed': 'The last listing failed.',
    // Rendered under the failure above, over the table that a PREVIOUS run
    // produced. It has to name a date: without it the table sits under "the
    // last listing failed" and reads as that run's result.
    'backups.setsFromEarlierRun':
      'Inventory of {{date}}, the last one that succeeded.',
    'backups.openInArgo': 'Open this run in Argo Workflows',
    // The way out when there is no run to name: the first read of the card
    // failed, so it links to the namespace's run list rather than to one run.
    'backups.openNamespaceInArgo':
      'Open the runs of this environment in Argo Workflows',
    // Retries the card's own read, not the workflow: the first read is the only
    // one that leaves nothing on screen, so it is the only one needing a button.
    'backups.retry': 'Try again',
    'backups.noBackups': 'No backup in the bucket for this environment.',
    'backups.rawFallback':
      'This listing did not come back in the expected format. Here it is as it was received.',
    'backups.logPruned':
      'The log of this listing is gone — Argo Workflows only keeps its last twenty runs. Start a new one.',
    'backups.logUnreadable': 'The log of this listing could not be read.',
    'backups.columnTimestamp': 'Backup',
    'backups.columnDatabases': 'Databases',
    'backups.columnVolumes': 'Volumes',
    'backups.start': 'Start a listing',
    // The same button once a run exists. Deliberately NOT `card.refresh`: this
    // one submits a workflow, so it keeps the verb of `backups.logPruned`
    // rather than borrowing the vocabulary of a display refresh.
    'backups.rerun': 'Start a new listing',
    'backups.alreadyRunning':
      'A listing is already running for this environment.',
    // The tooltip of the disabled button, and the answer to a 403 that arrived
    // anyway. Two keys and not one: the first explains a button that cannot be
    // pressed, the second explains a click that was refused, and folding them
    // together would make one of the two read as an error or as advice.
    'backups.submitNotAllowed':
      'Starting a listing is reserved to the administrator groups.',
    'backups.submitForbidden':
      'You are not allowed to start a listing for this environment.',
    // The tooltip of the listing button while a restore runs — the mirror of
    // `backups.restoreBlockedByListing` below, and worded like it: a wait, not
    // a refusal of the user.
    'backups.listingBlockedByRestore':
      'Wait for the restore in progress to finish before starting a listing.',
    'backups.refreshFailed': 'The listing could not be started.',
    'backups.unavailable': 'The backups cannot be read right now.',
    // The restore, which is the one destructive act of this card. "Restore"
    // throughout for the act; the confirmation says what it overwrites rather
    // than asking "are you sure", which nobody reads.
    'backups.columnAction': 'Action',
    'backups.restore': 'Restore',
    'backups.restoreConfirmTitle': 'Restore the backup of {{date}}?',
    'backups.restoreConfirmBody':
      'The databases of the {{namespace}} branch will be overwritten by this backup. A safety backup is taken just before.',
    // The one thing that cannot be guessed from the button. "Not necessarily",
    // because it depends on the environment: the workflow stops the workloads
    // only when the restore touches a PVC, and an Odoo branch declares
    // `pvcs: []` — so on Odoo it is never stopped, and the sentence has to
    // hold for both.
    'backups.restoreNotStopped':
      'Odoo is not necessarily stopped during the restore: restart the pod once it is over.',
    'backups.restoreConfirmAction': 'Restore for real',
    'backups.restoreCancel': 'Cancel',
    'backups.restoreRunning': 'Restore in progress…',
    // The refusal, not the state: the tooltip of a row's disabled button while
    // a restore is in flight. Worded after `backups.alreadyRunning`, which does
    // the same job for the listing — see the rule stated above
    // `backups.submitNotAllowed`.
    'backups.restoreAlreadyRunning':
      'A restore is already running for this environment.',
    'backups.restoreSucceeded': 'The restore finished.',
    'backups.restoreFailed': 'The last restore failed.',
    // The tooltips of a disabled button, one per reason, and the answer to a
    // 403 that arrived anyway — the same split as `backups.submitNotAllowed`
    // and `backups.submitForbidden` above.
    'backups.restoreNotAllowed':
      'Restoring is reserved to the administrator groups.',
    'backups.restoreNotConfigured':
      'No restore is configured for this environment.',
    'backups.restoreBlockedByListing':
      'Wait for the listing in progress to finish before restoring.',
    'backups.restoreForbidden':
      'You are not allowed to restore a backup for this environment.',
    'backups.restoreFailedToStart': 'The restore could not be started.',
    'backups.restoreLastRun': 'Last restore: {{date}}',
    // The upload panel. "Send" throughout for the act, never "copy": the copy
    // is what the workflow does inside the cluster, and the user's act is
    // handing a file over.
    'upload.title': 'Send a file',
    // "pod" and not "container", because a pod is what the picker below offers.
    // The file does land in a container — the workflow's `containerName` is left
    // empty so it takes the first one — but naming that here would describe a
    // choice this card never lets anyone make.
    'upload.subtitle': 'Copy a file into a running pod of this environment',
    'upload.notConfigured': 'No upload is configured for this environment.',
    'upload.podLabel': 'Destination pod',
    'upload.noPods': 'No running pod to send a file to.',
    'upload.fileLabel': 'File',
    'upload.targetPathLabel': 'Destination directory',
    'upload.targetPathInvalid':
      'The destination must be an absolute directory, with no `..` segment and no shell character.',
    'upload.submit': 'Send',
    // The label the button takes while a send is in flight, next to BUI's own
    // spinner. It replaces the separate "in progress" line that used to sit
    // beside the button, so there is one indicator and it is on the control
    // that was pressed.
    'upload.submitting': 'Sending…',
    'upload.lastRun': 'Last send: {{date}}',
    // The history register: the run on screen is the last one the ENVIRONMENT
    // saw, not one this session started — days old, or a colleague's. Same
    // three cases as the confirmation sentences below (generic, detailed,
    // failed), but told as a dated fact and in one line, the date being part of
    // the sentence instead of a separate `upload.lastRun` line under it.
    'upload.lastRunHistory': 'Last send: {{date}}.',
    'upload.lastRunHistoryDetails':
      'Last send: {{date}} — {{file}} copied into {{path}} on pod {{pod}}.',
    'upload.lastRunHistoryFailed': 'Last send: {{date}} — it failed.',
    'upload.noRun': 'No file has been sent to this environment yet.',
    // The fallback wording, for a run that recorded nothing about what it
    // copied — one submitted from the Argo UI, typically.
    'upload.runSucceeded': 'The last file was copied into the pod.',
    // What is worth saying when the run did record it: which file went where.
    // `path` is the destination DIRECTORY and `file` the name the copy took in
    // it, which is the name that was picked in the form.
    'upload.runSucceededDetails':
      'The file {{file}} was copied into {{path}} on pod {{pod}}.',
    'upload.runFailed': 'The last send failed.',
    'upload.alreadyRunning': 'A send is already running for this environment.',
    // The tooltip of the disabled button, and the answer to a 403 that arrived
    // anyway. Two keys and not one, for the reason the backups pair states.
    'upload.submitNotAllowed':
      'Sending a file is reserved to the administrator groups.',
    'upload.submitForbidden':
      'You are not allowed to send a file to this environment.',
    'upload.submitFailed': 'The file could not be sent.',
    'upload.tooLarge': 'This file is too large: the limit is 2 GiB.',
    // `reason` is the backend's own code — invalidPodName, invalidTargetPath,
    // fieldOrder, noFile — kept verbatim so a refusal can be traced back to the
    // check that made it. All four mean the form did not match its own contract,
    // which is a bug on our side rather than something the user can act on, so a
    // sentence per code would say no more than the code does.
    'upload.rejected': 'This send was refused by the server ({{reason}}).',
    // The fifth code, `noSuchPod`, gets a sentence of its own because it is the
    // only one a user can actually meet and act on: the pod list comes from the
    // page's resource-tree read, and a redeploy while the page is open renames
    // every pod. "Refresh" is the word on the page's own button, not a synonym.
    'upload.rejectedNoSuchPod':
      'This pod no longer exists. Refresh the page to see the current pods of this environment.',
    // The label of the disclosure the log sits behind. Collapsed by default —
    // it is a developer's tool, not the result of the send — and open from the
    // start when the run failed.
    'upload.logToggle': 'Execution log',
    'upload.logPruned':
      'The log of this send is gone — Argo Workflows only keeps its last twenty runs.',
    'upload.logUnreadable': 'The log of this send could not be read.',
    'upload.openInArgo': 'Open this run in Argo Workflows',
    'upload.openNamespaceInArgo':
      'Open the runs of this environment in Argo Workflows',
    'upload.retry': 'Try again',
    'upload.unavailable': 'The sends cannot be read right now.',
    // The sidebar block listing the Odoo projects. Short by necessity: the
    // sidebar label is clipped at a fixed width, so anything longer than two
    // words is read as an ellipsis.
    'nav.projects': 'Projects',
    'nav.noBranches': 'No environment',
    'nav.loadingBranches': 'Loading…',
    'error.notJson':
      'Unexpected response from the ArgoCD API (not JSON): {{body}}',
    'error.noIdentity':
      'Not signed in to Authelia. Guest sign-in cannot query ArgoCD.',
  },
});

/**
 * The French messages. `full: true` makes exhaustiveness a compile error, so a
 * key added to the ref above without a translation here fails the build rather
 * than falling back to English at runtime.
 *
 * Typography: the typographic apostrophe (’, U+2019) everywhere, never the
 * straight one. Uniform inside this file on purpose — the card and the page
 * show these strings side by side, and two shapes of apostrophe on one screen
 * read as a typo. Note that @waadoo/backstage-i18n still uses the straight one
 * for the upstream plugins; aligning that package is a separate pass.
 */
const resource = createTranslationMessages({
  full: true,
  ref: odooEnvironmentsTranslationRef,
  messages: {
    'card.title': 'Environnements',
    'card.subtitle': 'Un environnement Odoo par branche de développement',
    'card.refresh': 'Rafraîchir',
    'card.empty':
      'Aucun environnement pour ce projet. Poussez une branche `dev-…` pour en créer un.',
    'column.branch': 'Branche',
    'column.ci': 'CI',
    'column.health': 'État',
    'column.sync': 'Synchronisation',
    'column.url': 'Adresse',
    'column.links': 'Liens',
    // Adjectival forms, agreeing with the implied masculine « pipeline », so the
    // six cell labels share one register instead of mixing nouns (Succès,
    // Échec) with participles (Annulé, Ignoré). It is also the wording GitLab's
    // own French UI uses for these statuses. The cell may link to GitHub
    // instead, whose French UI says « exécutions » rather than « pipelines » —
    // deliberately not followed here: the column is read as one list, and
    // labelling two rows differently because their branches live on different
    // forges would suggest a difference in status, not in hosting.
    'ci.success': 'Réussi',
    'ci.failed': 'Échoué',
    'ci.running': 'En cours',
    'ci.pending': 'En attente',
    'ci.canceled': 'Annulé',
    'ci.skipped': 'Ignoré',
    'ci.unavailable': 'Indisponible',
    // Same adjectival register as the CI labels above: the three status columns
    // are read side by side on one row, so « Réussi | Sain | Synchronisé » has
    // to hold together as a sentence fragment.
    //
    // « Absent » rather than a literal « Manquant » for Missing: ArgoCD means
    // the resource is not in the cluster, which is what absent says in French.
    'health.healthy': 'Sain',
    'health.progressing': 'En cours',
    'health.degraded': 'Dégradé',
    'health.suspended': 'Suspendu',
    'health.missing': 'Absent',
    'health.unknown': 'Inconnu',
    'sync.synced': 'Synchronisé',
    'sync.outOfSync': 'Désynchronisé',
    'sync.unknown': 'Inconnu',
    'row.openInArgocd': 'Ouvrir dans ArgoCD',
    'row.openTerminals': 'Ouvrir un terminal',
    'row.openInGrafana': 'Monitoring (Grafana)',
    'row.openPipelines': 'Pipelines CI/CD de cette branche',
    'row.openPipelineRun': 'Ouvrir ce pipeline',
    'terminal.sectionApp': 'Application',
    'terminal.sectionDb': 'Base de données',
    'terminal.loading': 'Chargement des pods…',
    'terminal.empty': 'Aucun pod en cours d’exécution',
    'terminal.error': 'Pods indisponibles',
    // La page par branche.
    'page.environment': 'Environnement',
    'page.project': 'Projet',
    'page.revision': 'Révision',
    // « Elle […] supprimée » : le pronom et l’accord reprennent la branche, pas
    // l’environnement. C’est la branche qui disparaît — l’environnement n’est
    // que ce qu’ArgoCD en déployait.
    //
    // La même phrase sert aux deux moments où l’on peut la constater : aucune
    // Application ne porte ce nom de branche, et l’Application a disparu entre
    // la requête liste et la requête détail. Les distinguer n’apprendrait rien
    // à qui lit l’écran.
    'page.notFound':
      'Aucun environnement pour la branche {{branch}} du projet {{project}}. Elle vient peut-être d’être supprimée.',
    // La carte d’accès, juste sous la carte d’identité : les identifiants
    // administrateur de l’environnement, lus dans le secret Kubernetes que le
    // chart génère avec lui. « Afficher » et non « Révéler » : c’est le mot des
    // champs de mot de passe du navigateur, donc un mot qui n’a pas à
    // s’apprendre.
    'credentials.title': 'Accès à Odoo',
    'credentials.subtitle': 'Identifiants administrateur de cet environnement',
    'credentials.login': 'Identifiant',
    'credentials.password': 'Mot de passe',
    'credentials.reveal': 'Afficher',
    'credentials.hide': 'Masquer',
    'credentials.copy': 'Copier',
    'credentials.copied': 'Copié',
    // Le secret est nommé plutôt que désigné par « un secret Kubernetes » :
    // l’endroit où vit le mot de passe est le propos de la carte, et le nom est
    // ce qui le rend vérifiable au `kubectl` par qui le souhaite.
    'credentials.source':
      'Généré avec l’environnement et conservé dans le secret Kubernetes {{secret}}.',
    // Un constat, pas un échec — le registre de `backups.notConfigured` : rien
    // n’est cassé, l’environnement n’a simplement pas encore été initialisé.
    'credentials.notConfigured':
      'Aucun identifiant administrateur pour cet environnement. Ils sont générés à sa première initialisation.',
    'credentials.unavailable':
      'Les identifiants ne peuvent pas être lus pour le moment.',
    'credentials.retry': 'Réessayer',
    // Le presse-papiers est indisponible hors contexte sécurisé et peut être
    // refusé ; la phrase doit dire quoi faire à la place, sans quoi le bouton
    // se lit comme cassé.
    'credentials.copyFailed':
      'Le navigateur a refusé la copie : affichez le mot de passe et sélectionnez-le.',
    // Le sélecteur de branche de l’en-tête. « Changer de branche » sur le
    // bouton — l’infinitif de l’action, comme « Ouvrir un terminal » ; jamais
    // « Basculer », qui en français suggère un état permuté et non un
    // déplacement d’un écran à l’autre.
    'page.switchBranch': 'Changer de branche',
    'page.branches': 'Branches',
    'history.title': 'Historique des déploiements',
    'history.subtitle':
      'Chaque révision déployée par ArgoCD sur cette branche, de la plus récente à la plus ancienne',
    'history.columnRevision': 'Révision',
    // « Déployée » s'accorde avec « révision », la colonne voisine, et non avec
    // un « déploiement » masculin qui n'apparaît nulle part dans le tableau.
    'history.columnDeployedAt': 'Déployée',
    'history.empty': 'Aucun déploiement enregistré pour cette branche.',
    'history.openRevision': 'Ouvrir cette révision sur la forge',
    // « Logs » n'est pas traduit, pour la raison qui laisse « Healthy » ou
    // « Synced » en anglais plus haut : l'onglet ArgoCD vers lequel ce panneau
    // renvoie s'appelle LOGS, et « Journaux » ferait diverger les deux écrans.
    'logs.title': 'Logs',
    'logs.subtitle':
      '{{lines}} dernières lignes de {{pod}}, lues une seule fois',
    'logs.noPods':
      'Aucun pod en cours d’exécution dont on puisse lire les logs.',
    'logs.unavailable':
      'Les logs ne peuvent pas être affichés ici. Ouvrez-les dans ArgoCD.',
    'logs.openInArgocd': 'Ouvrir les logs dans ArgoCD',
    // Le panneau des sauvegardes. « Inventaire » désigne partout l'exécution du
    // workflow, jamais « sauvegarde » : ce que le bouton lance est une lecture
    // du bucket, et l'appeler une sauvegarde laisserait croire que le clic en
    // crée une. « Bucket » n'est pas traduit — c'est le mot que le stockage
    // objet d'OVH emploie, y compris dans sa console française.
    'backups.title': 'Sauvegardes',
    'backups.subtitle': 'Jeux de sauvegarde disponibles pour cet environnement',
    'backups.notConfigured':
      'Aucune sauvegarde configurée pour cet environnement.',
    'backups.noRun':
      'Aucun inventaire n’a encore été fait. Lancez-en un pour voir les sauvegardes disponibles.',
    'backups.running': 'Inventaire en cours…',
    'backups.lastRun': 'Dernier inventaire : {{date}}',
    'backups.runFailed': 'Le dernier inventaire a échoué.',
    // Affichée sous l’échec ci-dessus, au-dessus du tableau produit par une
    // exécution PRÉCÉDENTE. Elle doit dater : sans elle, le tableau se lit
    // comme le résultat de l’inventaire qui vient d’échouer.
    'backups.setsFromEarlierRun':
      'Inventaire du {{date}}, le dernier qui a abouti.',
    'backups.openInArgo': 'Ouvrir cette exécution dans Argo Workflows',
    'backups.openNamespaceInArgo':
      'Ouvrir les exécutions de cet environnement dans Argo Workflows',
    // Relance la lecture de la carte, pas l’inventaire — d’où « Réessayer »
    // plutôt qu’un mot du vocabulaire des inventaires, qui laisserait croire
    // que le clic déclenche un traitement.
    'backups.retry': 'Réessayer',
    'backups.noBackups':
      'Aucune sauvegarde dans le bucket pour cet environnement.',
    'backups.rawFallback':
      'Cet inventaire n’est pas revenu dans le format attendu. Le voici tel qu’il a été reçu.',
    'backups.logPruned':
      'Le journal de cet inventaire n’existe plus — Argo Workflows ne conserve que ses vingt dernières exécutions. Relancez-en un.',
    'backups.logUnreadable': 'Le journal de cet inventaire n’a pas pu être lu.',
    'backups.columnTimestamp': 'Sauvegarde',
    'backups.columnDatabases': 'Bases de données',
    'backups.columnVolumes': 'Volumes',
    'backups.start': 'Lancer un inventaire',
    // « Relancer », le verbe même de « Relancez-en un » dans
    // `backups.logPruned` : l’instruction et le bouton qui l’exécute doivent se
    // lire avec le même mot. Surtout pas « Rafraîchir », qui en français
    // désigne la relecture de l’affichage, alors que ce clic crée un pod.
    'backups.rerun': 'Relancer un inventaire',
    'backups.alreadyRunning':
      'Un inventaire est déjà en cours pour cet environnement.',
    // L’infobulle du bouton désactivé : elle dit la règle, au présent, sans
    // reprocher quoi que ce soit à qui lit — c’est une question de droits, pas
    // une erreur de sa part.
    'backups.submitNotAllowed':
      'Le lancement d’un inventaire est réservé aux groupes d’administration.',
    // Le 403, qui n’arrive que si les droits ont changé pendant que la carte
    // était ouverte. « Vous n’avez pas le droit » et non « Accès refusé » : la
    // phrase doit se lire comme une explication, pas comme un rejet technique.
    'backups.submitForbidden':
      'Vous n’avez pas le droit de lancer un inventaire pour cet environnement.',
    // L’infobulle du bouton d’inventaire pendant qu’une restauration tourne :
    // le miroir de `backups.restoreBlockedByListing` plus bas, et calquée sur
    // elle — une attente, pas un reproche.
    'backups.listingBlockedByRestore':
      'Attendez la fin de la restauration en cours avant de lancer un inventaire.',
    'backups.refreshFailed': 'L’inventaire n’a pas pu être lancé.',
    'backups.unavailable':
      'Les sauvegardes ne peuvent pas être lues pour le moment.',
    // La restauration, seul acte destructeur de cette carte. « Restaurer » pour
    // l’acte ; la confirmation dit ce qui est écrasé plutôt que de demander
    // « êtes-vous sûr ? », que personne ne lit.
    'backups.columnAction': 'Action',
    'backups.restore': 'Restaurer',
    'backups.restoreConfirmTitle': 'Restaurer la sauvegarde du {{date}} ?',
    'backups.restoreConfirmBody':
      'Les bases de données de la branche {{namespace}} seront écrasées par cette sauvegarde. Une sauvegarde de sécurité est prise juste avant.',
    // Le seul point qui ne se devine pas. « Pas forcément », parce que ça
    // dépend de l’environnement : le workflow n’arrête les charges que si la
    // restauration touche un PVC, et une branche Odoo déclare `pvcs: []` — donc
    // sur Odoo il n’est jamais arrêté, et la phrase doit valoir pour les deux.
    'backups.restoreNotStopped':
      'Odoo n’est pas forcément arrêté pendant la restauration : redémarrez le pod une fois qu’elle est terminée.',
    // « Pour de bon » et non « Confirmer » : c’est le second clic, celui qui
    // écrase réellement, et il doit se lire comme tel.
    'backups.restoreConfirmAction': 'Restaurer pour de bon',
    'backups.restoreCancel': 'Annuler',
    'backups.restoreRunning': 'Restauration en cours…',
    // Le refus, et non l’état : l’infobulle du bouton désactivé d’une ligne
    // pendant qu’une restauration tourne. Calquée sur `backups.alreadyRunning`,
    // qui joue le même rôle pour l’inventaire — voir la règle énoncée au-dessus
    // de `backups.submitNotAllowed`.
    'backups.restoreAlreadyRunning':
      'Une restauration est déjà en cours pour cet environnement.',
    'backups.restoreSucceeded': 'La restauration est terminée.',
    'backups.restoreFailed': 'La dernière restauration a échoué.',
    'backups.restoreNotAllowed':
      'La restauration est réservée aux groupes d’administration.',
    'backups.restoreNotConfigured':
      'Aucune restauration n’est configurée pour cet environnement.',
    'backups.restoreBlockedByListing':
      'Attendez la fin de l’inventaire en cours avant de restaurer.',
    'backups.restoreForbidden':
      'Vous n’avez pas le droit de restaurer une sauvegarde pour cet environnement.',
    'backups.restoreFailedToStart': 'La restauration n’a pas pu être lancée.',
    'backups.restoreLastRun': 'Dernière restauration : {{date}}',
    // Le panneau d’envoi de fichier. « Envoi » désigne partout l’acte de
    // l’utilisateur ; « copie » est réservé à ce que le workflow fait dans le
    // cluster. « Pod » n’est pas traduit — c’est le mot de Kubernetes, celui
    // qu’affichent ArgoCD et le menu des terminaux juste à côté.
    'upload.title': 'Envoyer un fichier',
    'upload.subtitle':
      'Copier un fichier dans un pod en cours d’exécution de cet environnement',
    'upload.notConfigured': 'Aucun envoi configuré pour cet environnement.',
    'upload.podLabel': 'Pod de destination',
    'upload.noPods':
      'Aucun pod en cours d’exécution vers lequel envoyer un fichier.',
    'upload.fileLabel': 'Fichier',
    'upload.targetPathLabel': 'Répertoire de destination',
    // « Un chemin absolu vers un répertoire » et non « un répertoire absolu » :
    // en français, c’est le chemin qui est absolu, pas le répertoire.
    'upload.targetPathInvalid':
      'La destination doit être un chemin absolu vers un répertoire, sans segment « .. » ni caractère spécial du shell.',
    'upload.submit': 'Envoyer',
    // Le libellé que prend le bouton pendant l’envoi, à côté du témoin de
    // chargement de BUI. Il remplace la ligne « Envoi en cours… » qui se tenait
    // à part : un seul indicateur, sur la commande qui vient d’être actionnée.
    'upload.submitting': 'Envoi en cours…',
    'upload.lastRun': 'Dernier envoi : {{date}}',
    // Le registre du constat : ce qui est affiché est le dernier envoi qu’a
    // connu l’ENVIRONNEMENT, pas celui qu’on vient de faire — il peut dater, ou
    // être celui d’un collègue. D’où une seule ligne, datée, sans « votre » ni
    // aucune formule de confirmation, et sans reprendre `upload.lastRun` en
    // dessous : la date est déjà dans la phrase.
    'upload.lastRunHistory': 'Dernier envoi : {{date}}.',
    // « copié » s’accorde avec « fichier », placé en tête comme dans
    // `upload.runSucceededDetails` et pour la même raison : « {{file}} copié »
    // ferait dépendre l’accord d’un nom de fichier inconnu. La phrase reste
    // elliptique — participe sans auxiliaire —, ce qui la tient dans le registre
    // du relevé et non de la confirmation.
    'upload.lastRunHistoryDetails':
      'Dernier envoi : {{date}} — le fichier {{file}} copié dans {{path}} du pod {{pod}}.',
    // « il » reprend « envoi », et non l’exécution du workflow : c’est l’envoi
    // que la phrase vient de nommer.
    'upload.lastRunHistoryFailed': 'Dernier envoi : {{date}} — il a échoué.',
    'upload.noRun': 'Aucun fichier n’a encore été envoyé à cet environnement.',
    'upload.runSucceeded': 'Le dernier fichier envoyé a été copié dans le pod.',
    // « copié » s’accorde avec « fichier », qui ouvre la phrase — c’est aussi
    // pourquoi le nom du fichier n’est pas mis en tête : « {{file}} a été
    // copié » laisserait l’accord dépendre d’un nom de fichier inconnu.
    'upload.runSucceededDetails':
      'Le fichier {{file}} a été copié dans {{path}} du pod {{pod}}.',
    'upload.runFailed': 'Le dernier envoi a échoué.',
    'upload.alreadyRunning':
      'Un envoi est déjà en cours pour cet environnement.',
    // L’infobulle du bouton désactivé : elle dit la règle, au présent, sans
    // reprocher quoi que ce soit à qui lit.
    'upload.submitNotAllowed':
      'L’envoi d’un fichier est réservé aux groupes d’administration.',
    'upload.submitForbidden':
      'Vous n’avez pas le droit d’envoyer un fichier à cet environnement.',
    'upload.submitFailed': 'Le fichier n’a pas pu être envoyé.',
    'upload.tooLarge':
      'Ce fichier est trop volumineux : la limite est de 2 Gio.',
    // Le code du serveur est laissé tel quel entre parenthèses : la phrase se lit
    // toujours comme une phrase, et le code n’est là que pour retrouver le
    // contrôle qui a refusé. Il ne reste ici que des codes qui signalent un
    // formulaire non conforme à son propre contrat — donc un défaut de notre
    // côté, sur lequel qui lit ne peut rien.
    'upload.rejected': 'Cet envoi a été refusé par le serveur ({{reason}}).',
    // « Rafraîchissez la page » reprend le mot du bouton de l’en-tête
    // (`card.refresh` : « Rafraîchir »), qui relit justement l’arbre des
    // ressources d’où vient la liste des pods. L’instruction et le bouton qui
    // l’exécute doivent se lire avec le même verbe — même règle que
    // `backups.rerun` et `backups.logPruned` plus haut.
    'upload.rejectedNoSuchPod':
      'Ce pod n’existe plus. Rafraîchissez la page pour voir les pods actuels de cet environnement.',
    // « Journal d’exécution » et non « Logs » : le mot « journal » est déjà
    // celui des deux phrases voisines (`upload.logPruned`,
    // `upload.logUnreadable`), et le panneau qui garde « Logs » en anglais plus
    // haut le fait parce qu’il renvoie vers l’onglet LOGS d’ArgoCD — ce qui
    // n’est pas le cas ici.
    'upload.logToggle': 'Journal d’exécution',
    'upload.logPruned':
      'Le journal de cet envoi n’existe plus — Argo Workflows ne conserve que ses vingt dernières exécutions.',
    'upload.logUnreadable': 'Le journal de cet envoi n’a pas pu être lu.',
    'upload.openInArgo': 'Ouvrir cette exécution dans Argo Workflows',
    // Mot pour mot `backups.openNamespaceInArgo` : les deux liens visent la même
    // URL, la liste des exécutions du namespace, et Argo n’y filtre pas les
    // envois. « Les envois de cet environnement » promettrait une liste que la
    // page ouverte ne donne pas, et ferait dire deux choses différentes à deux
    // liens identiques posés l’un sous l’autre.
    'upload.openNamespaceInArgo':
      'Ouvrir les exécutions de cet environnement dans Argo Workflows',
    'upload.retry': 'Réessayer',
    // « L’état des envois » et non « les envois » : ce que la carte n’a pas pu
    // lire est le compte rendu du dernier envoi, pas les fichiers eux-mêmes.
    'upload.unavailable':
      'L’état des envois ne peut pas être lu pour le moment.',
    'nav.projects': 'Projets',
    // Au singulier, comme `card.empty` juste au-dessus le sous-entend : ce que
    // la ligne dit est qu’il n’y en a pas un seul, pas qu’une liste est vide.
    'nav.noBranches': 'Aucun environnement',
    'nav.loadingBranches': 'Chargement…',
    'error.notJson':
      'Réponse inattendue de l’API ArgoCD (ce n’est pas du JSON) : {{body}}',
    'error.noIdentity':
      'Vous n’êtes pas connecté via Authelia. Le mode invité ne peut pas interroger ArgoCD.',
  },
});

export const odooEnvironmentsFrTranslation = TranslationBlueprint.make({
  name: 'odoo-environments-fr',
  params: { resource },
});
